import { useEffect, Fragment } from "react";
import { List, ListItem, ListItemText, Divider, ListItemIcon } from '@mui/material';
import LabelIcon from '@mui/icons-material/Label';

import MyAlert from "~/mui/MyAlert";
import { useTrans } from '~/hooks/useTrans';
import { fillUrl, formatDate, getUrlHostname } from '~/utils/utils';
import { IUrlParams } from "~/interfaces";
import { IAdvancedBillingConsent } from "~/providers/interfaces";
import { apiGetProjectAdvancedBillingConsent } from "~/providers/api";
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";

interface IList {
	title: string;
	subtitle: string;
	link: string;
}

interface IState {
	dialogLoading: boolean;
	errorMsg: string;
	items: Array<IList>;
}

interface IAdminAdvancedBillingConsentModal {
	params: IUrlParams;
	onClose?: () => void;
}

export default function AdminAdvancedBillingConsentModal({
	params = null,
	onClose = () => {},
}: IAdminAdvancedBillingConsentModal) {
	const { trans } = useTrans();
	const { lang, isCzech } = useLang();
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		items: [],
	});

	function processItems(items: Array<IAdvancedBillingConsent>): Array<IList> {
		return items.map(item => {
			const validTill = new Date(item.validTill);
			const isActive = validTill.getTime() > Date.now();
			const dateTxt = formatDate(validTill, isCzech);
			const activeTxt = isActive ? trans("adminAdvancedBillingConsentModal.activeOn") : trans("adminAdvancedBillingConsentModal.activeOff");

			return {
				title: `${item.projectDescription}`,
				subtitle: `${dateTxt} (${activeTxt}) - ${item.acceptedByEmail}`,
				// todo - muze jit casem pryc
				link: fillUrl(item.projectAddress),
			};
		});
	}

	async function loadData() {
		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		const advancedBillingConsentData = await apiGetProjectAdvancedBillingConsent("admin", params.projectId, lang);

		updateState({
			dialogLoading: false,
			...advancedBillingConsentData.noErrors
				? {
					items: processItems(advancedBillingConsentData.data),
				}
				: {},
			errorMsg: advancedBillingConsentData.errors.join(", "),
		});
	}

	useEffect(() => {
		loadData();
	}, []);

	/* eslint-disable react/no-array-index-key */
	return <MyAlert title={trans("adminAdvancedBillingConsentModal.title")} onClose={onClose} dialogLoading={state.dialogLoading} errorMsg={state.errorMsg} cancelCaption={ trans("close") }>
		{ state.items.length > 0 && <List>
			{ state.items.map((itemData, ind) => <Fragment key={ind}>
				<ListItem>
					<ListItemIcon>
						<LabelIcon />
					</ListItemIcon>
					<ListItemText primary={itemData.title} sx={{ wordBreak: "break-word" }} secondary={<>
						<span>{ itemData.subtitle }</span><br />
						<a href={itemData.link} target="_blank" rel="noopener noreferrer">
							{ getUrlHostname(itemData.link) }
						</a>
					</>} />
				</ListItem>
				{ ind !== state.items.length - 1 && <Divider /> }
			</Fragment>) }
		</List> }
		{ state.items.length === 0 && <p>
			{ trans("adminAdvancedBillingConsentModal.empty") }
		</p> }
	</MyAlert>;
}
